/**
 * GuessInput + VerdictDisplay: M4 batch 6b commit 3.
 *
 * Audience-side guess input. The local player types a guess, hits Enter
 * (or clicks Send), and the frame is dispatched over the WS. Server
 * replies with one of three verdicts:
 *
 *   - `GUESS_CORRECT` (broadcast)  → "Got it!"
 *   - `GUESS_CLOSE`   (whisper)    → "You're close!"
 *   - `GUESS_MISS`    (whisper)    → "Miss" (echo of the guess text)
 *
 * The verdict is surfaced via `<VerdictDisplay>` which fades after 3s
 * and uses `aria-live="polite"` so screen readers announce the result
 * without stealing focus.
 *
 * Per PRD §3.4 + §5.12:
 *  - Drawer + spectator never see this component (parent gates).
 *  - Audience-correct sees this disabled (can't double-guess).
 *  - GUESS_CLOSE / GUESS_MISS only ever reach the guesser themselves.
 */

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { ClientControlFrame } from "../ws.js";
import { playSfx } from "../audio/sfx.js";
import { ScribbleButton } from "./ScribbleButton.js";
import { CheckMark } from "../marks/v1/CheckMark.js";
import { CrossMark } from "../marks/v1/CrossMark.js";

type Verdict = { kind: "correct" | "close" | "miss"; text: string; at: number };

// ---------------------------------------------------------------------------
// GuessInput
// ---------------------------------------------------------------------------

type GuessInputProps = {
  /** WS sender from RoomShell. */
  sendFrame: (frame: ClientControlFrame) => boolean;
  /**
   * When true, the input + Send button are disabled (audience-correct
   * already guessed correctly this turn).
   */
  disabled?: boolean;
  /**
   * Latest server verdict for the local player's guess. Drives the
   * <VerdictDisplay> below the input. Undefined → nothing to show.
   */
  lastVerdict?: Verdict;
};

export function GuessInput({ sendFrame, disabled = false, lastVerdict }: GuessInputProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(() => {
    if (disabled) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) return; // empty guesses ignored client-side
    const ok = sendFrame({ kind: "SUBMIT_GUESS", text: trimmed });
    if (ok) setValue("");
  }, [value, disabled, sendFrame]);

  const onKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <div class="guess-input" data-testid="guess-input">
      <div class="guess-input-row">
        <input
          ref={inputRef}
          type="text"
          class="guess-input-field"
          data-testid="guess-input-field"
          placeholder="Type your guess…"
          autoComplete="off"
          autoCorrect="off"
          spellcheck={false}
          maxLength={120}
          disabled={disabled}
          value={value}
          onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
          onKeyDown={onKeyDown}
          aria-label="Your guess"
        />
        {/* The real component (audit 2026-06-11): the old bare button
            borrowed the class but never got the wobble border or the
            ink-wet press the rest of the app's buttons have. */}
        <ScribbleButton
          variant="secondary"
          class="guess-input-submit"
          data-testid="guess-input-submit"
          onClick={submit}
          disabled={disabled || value.trim().length === 0}
        >
          Send
        </ScribbleButton>
      </div>
      <VerdictDisplay verdict={lastVerdict} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// VerdictDisplay
// ---------------------------------------------------------------------------

type VerdictDisplayProps = {
  verdict: Verdict | undefined;
  /** Fade duration in ms (default 3000). Tests override for fake-timer assertions. */
  fadeMs?: number;
};

const VERDICT_MESSAGES = {
  correct: "Got it!",
  close: "So close…",
  miss: "Not quite.",
} as const;

export function VerdictDisplay({ verdict, fadeMs = 3000 }: VerdictDisplayProps) {
  // Track which verdict identity we're currently showing so a fresh
  // GUESS_MISS resets the fade timer even if the kind is the same.
  const [shown, setShown] = useState<Verdict | undefined>(verdict);

  useEffect(() => {
    if (!verdict) {
      setShown(undefined);
      return;
    }
    setShown(verdict);
    // M5.T8: fire the correct-yip / wrong-pop SFX on each new verdict.
    // The verdict's `at` timestamp gives every guess a fresh identity
    // so dupes (same kind, same text) still re-fire.
    playSfx(verdict.kind === "correct" ? "correct" : "wrong");
    const id = setTimeout(() => setShown(undefined), fadeMs);
    return () => clearTimeout(id);
  }, [verdict, fadeMs]);

  return (
    <div
      class={`verdict-display ${shown ? `verdict-display--${shown.kind}` : "verdict-display--empty"}`}
      data-testid="verdict-display"
      data-verdict-kind={shown?.kind ?? "none"}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {shown && (
        <>
          {/* Ink marks carry the verdict (audit 2026-06-11), the splash hero
              established the ✓/✗ guess grammar; the live verdict now speaks it. */}
          {shown.kind === "correct" && (
            <span class="verdict-display-mark" aria-hidden="true">
              <CheckMark size={14} />
            </span>
          )}
          {shown.kind === "miss" && (
            <span class="verdict-display-mark" aria-hidden="true">
              <CrossMark size={12} />
            </span>
          )}
          <span class="verdict-display-label">{VERDICT_MESSAGES[shown.kind]}</span>
          {shown.text && shown.kind !== "correct" && (
            <span class="verdict-display-text"> &mdash; "{shown.text}"</span>
          )}
        </>
      )}
    </div>
  );
}
